// src/components/DashboardHeader/index.jsx
import React, { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Snackbar,
  Alert as MuiAlert,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ShieldIcon from "@mui/icons-material/Shield";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import VerifiedIcon from "@mui/icons-material/Verified";
import AddIcon from "@mui/icons-material/Add";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LanguageIcon from "@mui/icons-material/Language";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import { fetchUserData } from "../../utils/userUtils";
import OrganizationVerificationModal from "../OrganizationVerificationModal";
import { useUser } from "../../context/UserContext";
import { theme } from "../../theme/colors";
import {
  fetchUserStats,
  fetchVolunteerPreferences,
  getOrgVerificationStatus,
  uploadProfileImageBase64,
} from "../../utils/dashboardApi";
import {
  isLegacyUsernamePromptEnabled,
  logLegacyUsernamePromptEvent,
} from "../../utils/legacyUsernamePrompt";
import { isPoliticianAccount } from "../../utils/accountTypes";

const ADD_ADDRESS_GOLD = "#d4af37";
const VERIFY_CARD_EXPANDED_KEY = "verifyCardExpanded";

const brand = {
  primary: theme.primary,
  accent: theme.primary,
  black: "var(--dna-color-palette-black-800, #111)",
};

const VERIFIED_GOLD = "#8B6914";

/* ---------- MAIN: DashboardHeader (top section) ---------- */
const DashboardHeader = () => {
  const navigate = useNavigate();

  const {
    verifiedUser,
    userRepsBioguideID,
    userSenOneBioguideID,
    userSenTwoBioguideID,
    userFirstName,
    userLastName,
    entityName,
    profileImage,
    accountTypeName,
    isStripeVerified,
    politicianVerificationStatus,
    verificationRejectionReason,
    citizenManualVerificationStatus,
    user,
    requiresUsername,
    loadUserContext,
  } = useUser();

  const fileInputRef = useRef(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const [showOrgVerification, setShowOrgVerification] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userStats, setUserStats] = useState({
    followingCount: 0,
    followersCount: 0,
    postsCount: 0,
  });
  const [isOpenToVolunteering, setIsOpenToVolunteering] = useState(false);
  const [orgVerificationStatus, setOrgVerificationStatus] = useState(null);
  const legacyPromptLoggedRef = useRef(false);
  const [verifyCardExpanded, setVerifyCardExpanded] = useState(() => {
    try {
      const stored = localStorage.getItem(VERIFY_CARD_EXPANDED_KEY);
      if (stored !== null) return stored === "true";
    } catch {
      // ignore
    }
    return true;
  });

  const [formData, setFormData] = useState({
    district: "N/A",
    representativeName: "N/A",
    user_district_representative_bioguideid: "N/A",
    is_verified: false,
    address_line: null,
    state_code: null,
    bio: "",
    current_office: "",
    campaign_website: "",
  });

  useEffect(() => {
    const load = async () => {
      await fetchUserData(setFormData, setError, setLoading);
    };
    load();
  }, []);

  useEffect(() => {
    const loadStats = async () => {
      const stats = await fetchUserStats();
      setUserStats(stats);
    };
    loadStats();
  }, []);

  useEffect(() => {
    const loadVolunteerPreferences = async () => {
      const preferences = await fetchVolunteerPreferences();
      setIsOpenToVolunteering(Boolean(preferences?.is_interested));
    };
    loadVolunteerPreferences();
  }, []);

  useEffect(() => {
    const isOrgAccount = ["organization", "non_profit"].includes(accountTypeName || "");
    if (!isOrgAccount) return;

    const loadOrgStatus = async () => {
      try {
        const status = await getOrgVerificationStatus();
        setOrgVerificationStatus(status);
      } catch (err) {
        console.error("Error loading organization verification status:", err);
      }
    };

    loadOrgStatus();
  }, [accountTypeName]);

  // ---- Derived values (prefer context; fallback to fetchUserData) ----
  const isVerified =
    typeof verifiedUser === "boolean" ? verifiedUser : !!formData.is_verified;

  const repBioguideId =
    (userRepsBioguideID && userRepsBioguideID !== "N/A" && userRepsBioguideID) ||
    (formData.user_district_representative_bioguideid &&
    formData.user_district_representative_bioguideid !== "N/A"
      ? formData.user_district_representative_bioguideid
      : undefined);

  // Match mobile DashboardHeader getDisplayName: only organization / non_profit use entity name; municipality uses first+last
  const getDisplayName = () => {
    const isOrg =
      accountTypeName === "organization" || accountTypeName === "non_profit";
    if (isOrg) {
      const raw = (entityName || formData.organization_legal_name || "").trim();
      return raw || "Organization";
    }
    if (userFirstName || userLastName) {
      return [userFirstName, userLastName].filter(Boolean).join(" ").trim();
    }
    if (formData.first_name || formData.last_name) {
      return [formData.first_name, formData.last_name].filter(Boolean).join(" ").trim();
    }
    return user?.email?.split("@")[0] || "User";
  };

  // Match mobile: show human-readable account type; "user" / "citizen" -> "Citizen"
  const accountTypeLabel = (() => {
    if (!accountTypeName) return "Civix Account";
    const name = String(accountTypeName).toLowerCase();
    if (name === "user" || name === "citizen") return "Citizen";
    return String(accountTypeName).replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase());
  })();

  const orgVerificationLabel =
    orgVerificationStatus?.verification?.status === "pending"
      ? "Verification Pending"
      : null;

  const showVerifiedIconBesideName =
    isVerified || orgVerificationStatus?.verification?.status === "verified";

  const allRepsMissing = !repBioguideId && !userSenOneBioguideID && !userSenTwoBioguideID;
  const showLegacyUsernamePrompt =
    isLegacyUsernamePromptEnabled && Boolean(requiresUsername) && allRepsMissing;

  useEffect(() => {
    if (showLegacyUsernamePrompt && !legacyPromptLoggedRef.current) {
      logLegacyUsernamePromptEvent("shown", { surface: "web_dashboard_header" });
      legacyPromptLoggedRef.current = true;
    }
  }, [showLegacyUsernamePrompt]);

  const isPolitician = isPoliticianAccount(accountTypeName);
  const isMunicipalityAccount = () => accountTypeName === "municipality";

  const toggleVerifyCard = () => {
    const isOrg = ["organization", "non_profit"].includes(accountTypeName || "");
    if (isOrg && !isVerified) return;

    setVerifyCardExpanded((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(VERIFY_CARD_EXPANDED_KEY, String(next));
      } catch {
        // ignore
      }
      return next;
    });
  };

  const politicianStripeComplete = isPolitician && isStripeVerified;
  const politicianAwaitingReview = politicianStripeComplete && ["pending", "unsubmitted"].includes(politicianVerificationStatus);
  const politicianRejected = isPolitician && politicianVerificationStatus === "rejected";

  const isOrgAccount = ["organization", "non_profit"].includes(accountTypeName || "");
  const orgRejected = isOrgAccount && orgVerificationStatus?.verification?.status === "rejected";
  const isCitizenAccount = !isPolitician && !isOrgAccount;
  const citizenRejected = isCitizenAccount && !isVerified && !!verificationRejectionReason;
  const citizenPendingVerification = isCitizenAccount && !isVerified && citizenManualVerificationStatus === "pending";

  useEffect(() => {
    if (!isOrgAccount || isVerified) return;
    setVerifyCardExpanded(true);
    try {
      localStorage.setItem(VERIFY_CARD_EXPANDED_KEY, "true");
    } catch {
      // ignore
    }
  }, [isOrgAccount, isVerified]);

  const showVerifyPrompt =
    isVerified === false &&
    !isMunicipalityAccount() &&
    !politicianAwaitingReview &&
    !politicianRejected &&
    !orgRejected &&
    !citizenRejected &&
    !citizenPendingVerification;
  const pinVerifyCardOpen = isOrgAccount && !isVerified;
  const verifyCardIsOpen = pinVerifyCardOpen || verifyCardExpanded;
  const showActionCards = showVerifyPrompt || showLegacyUsernamePrompt || allRepsMissing;
  const actionRowSx = {
    width: "100%",
    backgroundColor: "#ffffff",
    border: "1px solid #cbd5e1",
    borderRadius: 2,
    p: 2,
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
    textDecoration: "none",
    transition: "background-color 150ms ease, border-color 150ms ease",
    "&:hover": { backgroundColor: "#f8fafc", borderColor: "#94a3b8" },
  };
  const addAddressCardSx = {
    width: "100%",
    display: "flex",
    alignItems: "center",
    gap: 1.25,
    borderRadius: 1.5,
    backgroundColor: "rgba(212, 175, 55, 0.22)",
    py: 1.75,
    pr: 1.75,
    pl: 1,
    border: "1.5px solid",
    borderColor: ADD_ADDRESS_GOLD,
    textDecoration: "none",
    transition: "background-color 150ms ease",
    "&:hover": { backgroundColor: "rgba(212, 175, 55, 0.32)" },
  };

  const handleImageFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setToast({ open: true, message: "Image must be under 5MB.", severity: "error" });
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const base64Data = reader.result;
      let contentType = "image/jpeg";
      if (base64Data.startsWith("data:")) {
        const matches = base64Data.match(/^data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+);base64,/);
        if (matches?.[1]) contentType = matches[1];
      }

      try {
        setUploadingImage(true);
        await uploadProfileImageBase64(base64Data, contentType);
        await loadUserContext();
        setToast({ open: true, message: "Profile photo updated!", severity: "success" });
      } catch {
        setToast({ open: true, message: "Failed to upload photo.", severity: "error" });
      } finally {
        setUploadingImage(false);
      }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const ActionRow = ({ icon, label, onClick, rightElement }) => (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        py: 1.5,
        borderBottom: "1px solid rgba(0,0,0,0.04)",
        cursor: onClick ? "pointer" : "default",
        "&:hover": onClick ? { backgroundColor: "rgba(0,0,0,0.02)" } : {},
      }}
    >
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: "50%",
          backgroundColor: "rgba(11, 31, 26, 0.06)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mr: 1.5,
        }}
      >
        {icon}
      </Box>
      <Typography variant="body2" sx={{ flex: 1, fontWeight: 500, color: brand.black, fontSize: "0.9rem" }}>
        {label}
      </Typography>
      {rightElement || <ChevronRightIcon sx={{ color: "#9ca3af", fontSize: 20 }} />}
    </Box>
  );

  return (
    <>
      {/* Profile row: image, display name, stats, account link */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          style={{ display: "none" }}
          onChange={handleImageFileSelect}
        />
        {profileImage ? (
          <Box
            component="img"
            src={profileImage}
            alt=""
            sx={{ width: 56, height: 56, borderRadius: "50%", objectFit: "cover", border: "2px solid", borderColor: "divider" }}
          />
        ) : (
          <Box
            onClick={() => !uploadingImage && fileInputRef.current?.click()}
            sx={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              bgcolor: "#f1f5f9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: brand.primary,
              fontWeight: 700,
              fontSize: "1.25rem",
              position: "relative",
              cursor: uploadingImage ? "default" : "pointer",
              border: "2px dashed #cbd5e1",
              "&:hover": uploadingImage ? {} : { bgcolor: "#e2e8f0", borderColor: brand.primary },
            }}
          >
            {uploadingImage ? (
              <CircularProgress size={24} sx={{ color: brand.primary }} />
            ) : (
              <Typography component="span" sx={{ fontWeight: 700, fontSize: "1.25rem", color: "#0f172a" }}>
                {(getDisplayName() || "U").charAt(0).toUpperCase()}
              </Typography>
            )}
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                right: 0,
                width: 20,
                height: 20,
                borderRadius: "50%",
                bgcolor: brand.primary,
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "2px solid #fff",
              }}
            >
              <AddIcon sx={{ fontSize: 14 }} />
            </Box>
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, color: brand.black, minWidth: 0 }}
              noWrap
            >
              {getDisplayName()}
            </Typography>
            {showVerifiedIconBesideName && (
              <VerifiedIcon
                sx={{
                  fontSize: 18,
                  color: VERIFIED_GOLD,
                  flexShrink: 0,
                }}
              />
            )}
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
            <Chip
              size="small"
              label={accountTypeLabel}
              sx={{
                fontWeight: 700,
                backgroundColor: `${brand.primary}12`,
                color: "#0f172a",
              }}
            />
            {isOpenToVolunteering && (
              <Chip
                size="small"
                label="Open to volunteering"
                variant="outlined"
                sx={{ fontWeight: 700 }}
              />
            )}
            {orgVerificationLabel && (
              <Chip
                size="small"
                label={orgVerificationLabel}
                variant="outlined"
                sx={{ fontWeight: 700 }}
              />
            )}
          </Stack>

          <Stack direction="row" spacing={3} sx={{ mt: 1.25 }}>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: brand.black }}>
                {userStats.postsCount}
              </Typography>
              <Typography variant="caption" sx={{ color: "#0f172a", letterSpacing: 0.4 }}>
                POSTS
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: brand.black }}>
                {userStats.followersCount}
              </Typography>
              <Typography variant="caption" sx={{ color: "#0f172a", letterSpacing: 0.4 }}>
                FOLLOWERS
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: brand.black }}>
                {userStats.followingCount}
              </Typography>
              <Typography variant="caption" sx={{ color: "#0f172a", letterSpacing: 0.4 }}>
                FOLLOWING
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Box>

      {/* Account Management Rows */}
      <Box sx={{ mb: 3 }}>
        <ActionRow
          icon={<AccountCircleOutlinedIcon sx={{ fontSize: 16, color: "#0b1f1a" }} />}
          label="Account Management"
          onClick={() => navigate("/account-details")}
        />

        {accountTypeName && (
          <ActionRow
            icon={<CampaignOutlinedIcon sx={{ fontSize: 16, color: "#0b1f1a" }} />}
            label="Campaigns"
            onClick={() => {
              navigate("/campaigns");
            }}
          />
        )}

        {/* Bio Section */}
        {formData.bio && formData.bio.trim() ? (
          <Box sx={{ mt: 2, p: 2, backgroundColor: "rgba(0,0,0,0.02)", borderRadius: 2 }}>
            <Typography variant="body2" sx={{ color: brand.black, lineHeight: 1.5 }}>
              {formData.bio}
            </Typography>
          </Box>
        ) : (
          <ActionRow
            icon={<EditOutlinedIcon sx={{ fontSize: 16, color: "#0b1f1a" }} />}
            label="Add Your Bio"
            onClick={() => navigate("/account-details")}
          />
        )}

        {/* Verification Pending banner for politicians awaiting admin review */}
        {politicianAwaitingReview && (
          <Box
            sx={{
              mt: 1,
              backgroundColor: "rgba(254, 243, 199, 0.4)",
              border: "1px solid rgba(251, 191, 36, 0.3)",
              borderRadius: 1.5,
              px: 1.5,
              py: 1.25,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a", fontSize: "0.8rem" }}>
              Verification Pending
            </Typography>
            <Typography variant="caption" sx={{ color: "#334155", display: "block", lineHeight: 1.5, mt: 0.5 }}>
              Your identity has been verified. Your account is now under review and will be approved shortly.
            </Typography>
          </Box>
        )}

        {/* Rejection notice for politicians whose verification was rejected */}
        {politicianRejected && (
          <Box
            sx={{
              mt: 1,
              backgroundColor: "rgba(254, 226, 226, 0.5)",
              border: "1px solid rgba(239, 68, 68, 0.3)",
              borderRadius: 1.5,
              px: 1.5,
              py: 1.25,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a", fontSize: "0.8rem" }}>
              Verification Not Approved
            </Typography>
            {verificationRejectionReason && (
              <Typography variant="caption" sx={{ color: "#334155", display: "block", lineHeight: 1.5, mt: 0.5 }}>
                Reason: {verificationRejectionReason}
              </Typography>
            )}
            <Typography variant="caption" sx={{ color: "#334155", display: "block", lineHeight: 1.5, mt: 0.5 }}>
              Please update your profile and resubmit for review.
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
              <Button
                onClick={() => navigate("/account-details")}
                variant="contained"
                size="small"
                sx={{
                  backgroundColor: "#991b1b",
                  color: "#fff",
                  px: 2.5,
                  py: 0.5,
                  fontWeight: 700,
                  textTransform: "none",
                  fontSize: "0.8rem",
                  "&:hover": { opacity: 0.9, backgroundColor: "#7f1d1d" },
                }}
              >
                Update Profile
              </Button>
            </Box>
          </Box>
        )}

        {/* Rejection notice for organizations */}
        {orgRejected && (
          <Box
            sx={{
              mt: 1,
              backgroundColor: "rgba(254, 226, 226, 0.5)",
              border: "1px solid rgba(239, 68, 68, 0.3)",
              borderRadius: 1.5,
              px: 1.5,
              py: 1.25,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a", fontSize: "0.8rem" }}>
              Organization Verification Not Approved
            </Typography>
            {verificationRejectionReason && (
              <Typography variant="caption" sx={{ color: "#334155", display: "block", lineHeight: 1.5, mt: 0.5 }}>
                Reason: {verificationRejectionReason}
              </Typography>
            )}
            <Typography variant="caption" sx={{ color: "#334155", display: "block", lineHeight: 1.5, mt: 0.5 }}>
              Please upload new documentation and resubmit your verification.
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
              <Button
                onClick={() => setShowOrgVerification(true)}
                variant="contained"
                size="small"
                sx={{
                  backgroundColor: "#991b1b",
                  color: "#fff",
                  px: 2.5,
                  py: 0.5,
                  fontWeight: 700,
                  textTransform: "none",
                  fontSize: "0.8rem",
                  "&:hover": { opacity: 0.9, backgroundColor: "#7f1d1d" },
                }}
              >
                Update Verification
              </Button>
            </Box>
          </Box>
        )}

        {/* Pending verification banner for citizens */}
        {citizenPendingVerification && !citizenRejected && (
          <Box
            sx={{
              mt: 1,
              backgroundColor: "rgba(254, 243, 199, 0.4)",
              border: "1px solid rgba(251, 191, 36, 0.3)",
              borderRadius: 1.5,
              px: 1.5,
              py: 1.25,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a", fontSize: "0.8rem" }}>
              Verification Pending
            </Typography>
            <Typography variant="caption" sx={{ color: "#334155", display: "block", lineHeight: 1.5, mt: 0.5 }}>
              Your verification is being reviewed by our team. You'll be notified once it's approved.
            </Typography>
          </Box>
        )}

        {/* Rejection notice for citizens (manual verification) */}
        {citizenRejected && (
          <Box
            sx={{
              mt: 1,
              backgroundColor: "rgba(254, 226, 226, 0.5)",
              border: "1px solid rgba(239, 68, 68, 0.3)",
              borderRadius: 1.5,
              px: 1.5,
              py: 1.25,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, color: "#0f172a", fontSize: "0.8rem" }}>
              Verification Not Approved
            </Typography>
            {verificationRejectionReason && (
              <Typography variant="caption" sx={{ color: "#334155", display: "block", lineHeight: 1.5, mt: 0.5 }}>
                Reason: {verificationRejectionReason}
              </Typography>
            )}
            <Typography variant="caption" sx={{ color: "#334155", display: "block", lineHeight: 1.5, mt: 0.5 }}>
              You can try verifying again through Stripe Identity or submit a new manual verification.
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
              <Button
                onClick={() => navigate("/verify-identity")}
                variant="contained"
                size="small"
                sx={{
                  backgroundColor: "#991b1b",
                  color: "#fff",
                  px: 2.5,
                  py: 0.5,
                  fontWeight: 700,
                  textTransform: "none",
                  fontSize: "0.8rem",
                  "&:hover": { opacity: 0.9, backgroundColor: "#7f1d1d" },
                }}
              >
                Try Again
              </Button>
            </Box>
          </Box>
        )}

        {/* Politician: Current Office */}
        {isPolitician && formData.current_office && (
          <Box sx={{ display: "flex", alignItems: "center", py: 1.5, borderBottom: "1px solid rgba(0,0,0,0.04)" }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: brand.black, mr: 1 }}>
              Elected Office:
            </Typography>
            <Typography variant="body2" sx={{ color: brand.black }}>
              {formData.current_office}
            </Typography>
          </Box>
        )}

        {/* Politician: Campaign Website */}
        {isPolitician && formData.campaign_website && (
          <Box
            component="a"
            href={formData.campaign_website.startsWith('http') ? formData.campaign_website : `https://${formData.campaign_website}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: "flex",
              alignItems: "center",
              py: 1.5,
              borderBottom: "1px solid rgba(0,0,0,0.04)",
              textDecoration: "none",
              "&:hover": { backgroundColor: "rgba(0,0,0,0.02)" },
            }}
          >
            <LanguageIcon sx={{ fontSize: 16, color: brand.primary, mr: 1 }} />
            <Typography variant="body2" sx={{ flex: 1, fontWeight: 500, color: brand.black }}>
              Website
            </Typography>
            <OpenInNewIcon sx={{ fontSize: 14, color: brand.primary }} />
          </Box>
        )}
      </Box>

      {showActionCards && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
          {allRepsMissing && (
            <Box component={Link} to="/setup-representative" sx={addAddressCardSx}>
              <LocationOnOutlinedIcon
                sx={{ fontSize: 22, color: "#0f172a", flexShrink: 0 }}
                aria-hidden
              />
              <Typography
                variant="body2"
                sx={{ flex: 1, color: "#0f172a", fontWeight: 600, fontSize: 15, lineHeight: 1.45 }}
              >
                See upcoming elections and your representatives
              </Typography>
              <Box
                sx={{
                  backgroundColor: ADD_ADDRESS_GOLD,
                  borderRadius: 1,
                  px: 1.5,
                  py: 1,
                  minHeight: 44,
                  display: "flex",
                  alignItems: "center",
                  flexShrink: 0,
                }}
              >
                <Typography variant="body2" sx={{ color: "#0f172a", fontWeight: 600, fontSize: 13 }}>
                  Add Address
                </Typography>
              </Box>
            </Box>
          )}

          {showVerifyPrompt && (
            <Box
              sx={{
                width: "100%",
                bgcolor: "#ffffff",
                borderRadius: "12px",
                border: "1px solid #cbd5e1",
                px: 1.5,
                py: 1.25,
              }}
            >
              <Box
                component={pinVerifyCardOpen ? "div" : "button"}
                type={pinVerifyCardOpen ? undefined : "button"}
                onClick={pinVerifyCardOpen ? undefined : toggleVerifyCard}
                aria-expanded={verifyCardIsOpen}
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.25,
                  border: "none",
                  bgcolor: "transparent",
                  cursor: pinVerifyCardOpen ? "default" : "pointer",
                  textAlign: "left",
                  p: 0.5,
                  borderRadius: 1,
                  "&:hover": { bgcolor: pinVerifyCardOpen ? "transparent" : "rgba(0,0,0,0.02)" },
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <ShieldIcon sx={{ fontSize: 20, color: theme.primary }} aria-hidden />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: 14, fontWeight: 600, color: "#0f172a", lineHeight: 1.3, mb: 0.25 }}
                  >
                    Verify Your Identity
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: 12, fontWeight: 500, color: "#0f172a", lineHeight: 1.35 }}
                  >
                    {verifyCardIsOpen
                      ? "Credibility is key to our mission."
                      : "Click to learn more"}
                  </Typography>
                </Box>
                {!pinVerifyCardOpen && (
                  verifyCardIsOpen ? (
                    <ExpandLessIcon sx={{ color: "#0f172a", fontSize: 20, flexShrink: 0 }} aria-hidden />
                  ) : (
                    <ExpandMoreIcon sx={{ color: "#0f172a", fontSize: 20, flexShrink: 0 }} aria-hidden />
                  )
                )}
              </Box>

              {verifyCardIsOpen && (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    px: 0.5,
                    pt: 0.75,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      flex: "1 1 12rem",
                      minWidth: 0,
                      fontSize: 14,
                      color: "#0f172a",
                      lineHeight: 1.5,
                      m: 0,
                    }}
                  >
                    {isOrgAccount
                      ? "Verify your organization to unlock full features and build trust with the community."
                      : "We verify identities to keep CivixThread a credible space for real civic participation."}
                  </Typography>
                  <Button
                    onClick={() => {
                      if (isOrgAccount) {
                        setShowOrgVerification(true);
                      } else {
                        navigate("/verify-identity");
                      }
                    }}
                    disabled={isOrgAccount && orgVerificationStatus?.verification?.status === "pending"}
                    endIcon={<ArrowForwardIcon sx={{ fontSize: 18 }} />}
                    sx={{
                      flexShrink: 0,
                      minHeight: 40,
                      px: 2,
                      py: 0.75,
                      borderRadius: "8px",
                      background: theme.gradient,
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: 14,
                      textTransform: "none",
                      boxShadow: "none",
                      whiteSpace: "nowrap",
                      "&:hover": { background: theme.gradient, opacity: 0.92, boxShadow: "none" },
                      "&.Mui-disabled": { color: "#fff", opacity: 0.7 },
                    }}
                  >
                    {isOrgAccount && orgVerificationStatus?.verification?.status === "pending"
                      ? "Pending Verification"
                      : "Get Verified"}
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {showLegacyUsernamePrompt && (
            <Box
              component="button"
              type="button"
              onClick={() => {
                logLegacyUsernamePromptEvent("cta_clicked", {
                  surface: "web_dashboard_header",
                  destination: "/username-selection",
                });
                navigate("/username-selection", { state: { fromDashboard: true } });
              }}
              sx={actionRowSx}
            >
              <Typography variant="body2" sx={{ color: "#0f172a", fontWeight: 700 }}>
                Add a username to complete your profile
              </Typography>
              <ChevronRightIcon sx={{ color: "#334155", fontSize: 20 }} />
            </Box>
          )}
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {String(error)} {loading ? " (loading…)" : ""}
        </Alert>
      )}

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <MuiAlert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="filled"
          sx={{ borderRadius: 2 }}
        >
          {toast.message}
        </MuiAlert>
      </Snackbar>

      <OrganizationVerificationModal
        isOpen={showOrgVerification}
        onClose={() => setShowOrgVerification(false)}
      />
    </>
  );
};

export default DashboardHeader;
